import React, { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { getPathArray } from "../../helpers/getPathArray";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  BarChart,
  Bar,
  LabelList,
  Cell,
} from "recharts";
import { AuthState } from "../../store/auth/types";
import { connect } from "react-redux";
import _ from "lodash";
import "./BasicAnalytics.scss";
import { GetCurrentStoreData, WebsitesState } from "../../store/websites/types";
import { getCurrentStoreData } from "../../store/websites/thunks";
import { getAnalyticsDataAsync } from "../../helpers/async/getAnalyticsDataAsync";
import SpinnerBox from "../dashboard/SpinnerBox";
import { getSubdomain } from "../../helpers/custom_domain/getSubdomain";
import { Empty, Icon, Spin } from "antd";
import { getActiveDomain } from "../../helpers/websites/getActiveDomain";
interface Props extends RouteComponentProps {
  auth: AuthState;
  history: any;
  websites: WebsitesState;
  getCurrentStoreData: GetCurrentStoreData;
}

interface ItemCardProps {
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
  bigText?: string;
  style?: React.CSSProperties;
}
const ItemCard = (props: ItemCardProps) => {
  return (
    <div className="basic-analytics__item-card" style={props.style}>
      <div className="basic-analytics__item-card-text">
        {!!props.title && (
          <h3 className="basic-analytics__item-card-text-title">
            {props.title}
          </h3>
        )}
        {props.bigText && (
          <p className="basic-analytics__item-card-text-big-text">
            {props.bigText}
          </p>
        )}
        {!!props.subtitle && (
          <p className="basic-analytics__item-card-text-subtitle">
            {props.subtitle}
          </p>
        )}
      </div>
      <div className="basic-analytics__item-card-content">{props.children}</div>
    </div>
  );
};

interface ViewsChartProps extends Props {
  data: any;
  setData: (data: any) => void;
}
const ViewsChart = (props: ViewsChartProps) => {
  const { data, setData } = props;
  const [isLoading, setIsLoading] = useState(true);

  const getTotalViewsData = () => {
    let customDomain = _.get(
      props.getCurrentStoreData(),
      "currentWebsite.custom_domain",
      ""
    );
    let prefix = getSubdomain(customDomain) ? "" : "www.";
    customDomain = `${prefix}${customDomain}`;
    getAnalyticsDataAsync({
      accessToken: props.auth.accessToken,
      data: {
        domain: customDomain,
        type: "views",
      },
    })
      .then((response) => {
        // console.log("response.data: ", response.data);
        const rawData = _.get(response.data, "data", []);

        // Create a map of existing data points for easy lookup
        const dataMap = new Map();
        rawData.forEach((item: any) => {
          const date = new Date(item.t);
          const key = `${date.getMonth() + 1}-${date.getDate()}`;
          dataMap.set(key, parseInt(item.count));
        });

        // Generate 30 days of data
        const today = new Date();
        const formattedData = Array.from({ length: 30 }, (_, i) => {
          const date = new Date(today);
          date.setDate(date.getDate() - (29 - i));

          const month = date.toLocaleString("default", { month: "short" });
          const day = date.getDate();
          const key = `${date.getMonth() + 1}-${date.getDate()}`;

          return {
            name: `${month} ${day}`,
            views: dataMap.get(key) || 0,
          };
        });

        setData(formattedData);
      })
      .catch((error) => {
        console.error(error);
        console.error(error.response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getTotalViewsData();
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon type="loading" style={{ fontSize: 24, color: "#40A9FF" }} />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
        }}
      >
        <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{
          top: 0,
          right: 0,
          left: 4,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#303030" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#303030" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#F0F3F7" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="name"
          interval="preserveStartEnd"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 12, fill: "#76767b" }}
        />
        <Tooltip
          cursor={false}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const month = payload[0].payload.name;
              return (
                <div
                  style={{
                    background: "white",
                    padding: "4px 8px",
                    border: "1px solid #F0F4F7",
                    borderRadius: "6px",
                    boxShadow:
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 400,
                      marginBottom: "2px",
                      lineHeight: "1.2",
                    }}
                  >
                    {month}
                  </div>
                  <div
                    style={{
                      opacity: 0.6,
                      fontSize: "12px",
                      lineHeight: "1.2",
                    }}
                  >
                    {payload[0].value} <span>views</span>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <Area
          type="monotone"
          dataKey="views"
          stroke="#303030"
          fill="url(#areaGradient)"
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

interface PageViewsChartProps extends Props {
  data: any;
  setData: (data: any) => void;
}
const PageViewsChart = (props: PageViewsChartProps) => {
  const { data, setData } = props;
  const [isLoading, setIsLoading] = useState(true);

  const getTotalViewsData = () => {
    let customDomain = _.get(
      props.getCurrentStoreData(),
      "currentWebsite.custom_domain",
      ""
    );
    let prefix = getSubdomain(customDomain) ? "" : "www.";
    customDomain = `${prefix}${customDomain}`;
    getAnalyticsDataAsync({
      accessToken: props.auth.accessToken,
      data: {
        domain: customDomain,
        type: "pages",
      },
    })
      .then((response) => {
        // console.log("response.data: ", response.data);
        const rawData = _.get(response.data, "data", []);

        // Create a map to store unique paths with their maximum counts
        const pathMap = new Map();

        rawData.forEach((item: any) => {
          // Trim slashes from start and end
          const trimmedPath = item.path.replace(/^\/+|\/+$/g, "");

          // Skip unwanted paths
          if (trimmedPath === "robots.txt" || trimmedPath === "sitemap.xml") {
            return;
          }
          if (
            (trimmedPath || "").toString().includes(".txt") ||
            (trimmedPath || "").toString().includes(".xml") ||
            (trimmedPath || "").toString().includes(".js") ||
            (trimmedPath || "").toString().includes(".css")
          ) {
            return;
          }
          const domain = getActiveDomain(
            props.getCurrentStoreData().currentWebsite
          );
          if ((trimmedPath || "").toString().includes(domain)) {
            return;
          }
          if (trimmedPath === "?") {
            return;
          }
          const count = parseInt(item.count);
          const existingCount = pathMap.get(trimmedPath) || 0;

          // Keep the path with the highest count
          if (count > existingCount) {
            pathMap.set(trimmedPath, count);
          }
        });

        // Convert map to array and format objects
        const formattedData = Array.from(pathMap.entries())
          .map(([path, count]) => ({
            name: path ? `/${path}` : "Home", // Use 'Home' for empty path
            views: count,
          }))
          .sort((a, b) => b.views - a.views); // Sort by views in descending order

        setData(formattedData);
      })
      .catch((error) => {
        console.error(error);
        console.error(error.response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getTotalViewsData();
  }, []);

  const truncateText = (text: string, maxLength: number = 10) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const formattedData = data.map((item: any) => ({
    ...item,
    displayName: truncateText(item.name),
  }));

  if (isLoading) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon type="loading" style={{ fontSize: 24, color: "#40A9FF" }} />
      </div>
    );
  }

  if (formattedData.length === 0) {
    return (
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
        }}
      >
        <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={formattedData}
        layout="vertical"
        margin={{ top: 0, right: 88, left: 0, bottom: 0 }}
        style={{ background: "transparent" }}
      >
        <XAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#76767b" }}
          hide
        />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#76767b" }}
          hide
        />
        <Tooltip
          cursor={false}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const month = payload[0].payload.name;
              return (
                <div
                  style={{
                    background: "white",
                    padding: "4px 8px",
                    border: "1px solid #F0F4F7",
                    borderRadius: "6px",
                    boxShadow:
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 400,
                      marginBottom: "2px",
                      lineHeight: "1.2",
                    }}
                  >
                    {month}
                  </div>
                  <div
                    style={{
                      opacity: 0.6,
                      fontSize: "12px",
                      lineHeight: "1.2",
                    }}
                  >
                    {payload[0].value} <span>views</span>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <Bar dataKey="views" fill="#303030" radius={6} minPointSize={100}>
          <LabelList
            dataKey="displayName"
            position="insideLeft"
            offset={8}
            fontSize={12}
            fill="#fff"
          />
          <LabelList
            dataKey="views"
            position="right"
            offset={8}
            fontSize={12}
            fill="#303030"
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

interface CountriesChartProps extends Props {
  data: any;
  outerRadius?: number;
  setData: (data: any) => void;
}
const CountriesChart = (props: CountriesChartProps) => {
  const { data, outerRadius, setData } = props;
  const [isLoading, setIsLoading] = useState(true);
  // Color palette for pie chart segments
  const colors = [
    "#303030", // Blue (kept as is)
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#8B5CF6", // Indigo
    "#EC4899", // Pink
    "#14B8A6", // Teal
    "#F97316", // Orange
    "#6366F1", // Indigo
    "#06B6D4", // Cyan
    "#A855F7", // Purple
  ];

  const getCountriesData = () => {
    // Use Intl.DisplayNames for country name localization
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    let customDomain = _.get(
      props.getCurrentStoreData(),
      "currentWebsite.custom_domain",
      ""
    );
    let prefix = getSubdomain(customDomain) ? "" : "www.";
    customDomain = `${prefix}${customDomain}`;

    getAnalyticsDataAsync({
      accessToken: props.auth.accessToken,
      data: {
        domain: customDomain,
        type: "countries",
      },
    })
      .then((response) => {
        // console.log("response.data: ", response.data);
        const rawData = _.get(response.data, "data", []);

        const formattedData = rawData
          .map((item: any) => {
            try {
              const a = undefined;
              return {
                name: regionNames.of(item.country) || item.country,
                value: parseInt(item.count),
              };
            } catch (e) {
              // Fallback to original country code if translation fails
              return {
                name: item.country,
                value: parseInt(item.count),
              };
            }
          })
          .sort((a, b) => b.value - a.value);

        setData(formattedData);
      })
      .catch((error) => {
        console.error(error);
        console.error(error.response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getCountriesData();
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon type="loading" style={{ fontSize: 24, color: "#40A9FF" }} />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
        }}
      >
        <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
      }}
    >
      <ResponsiveContainer width="60%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={outerRadius || 88}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${entry.name}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const month = payload[0].payload.name;
                return (
                  <div
                    style={{
                      background: "white",
                      padding: "4px 8px",
                      border: "1px solid #F0F4F7",
                      borderRadius: "6px",
                      boxShadow:
                        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 400,
                        marginBottom: "2px",
                        lineHeight: "1.2",
                      }}
                    >
                      {month}
                    </div>
                    <div
                      style={{
                        opacity: 0.6,
                        fontSize: "12px",
                        lineHeight: "1.2",
                      }}
                    >
                      {payload[0].value} <span>views</span>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "12px",
          width: "40%",
          padding: "0 16px",
        }}
      >
        {data.map((entry, index) => (
          <div
            key={entry.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                backgroundColor: colors[index % colors.length],
                borderRadius: "2px",
              }}
            />
            <span
              style={{
                fontSize: "12px",
                color: "#76767b",
                whiteSpace: "nowrap",
              }}
            >
              {entry.name} ({entry.value})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface ReferrersChartProps extends Props {
  data: any;
  setData: (data: any) => void;
}
const ReferrersChart = (props: ReferrersChartProps) => {
  const { data, setData } = props;
  const [isLoading, setIsLoading] = useState(true);

  const getReferrersData = () => {
    let customDomain = _.get(
      props.getCurrentStoreData(),
      "currentWebsite.custom_domain",
      ""
    );
    let prefix = getSubdomain(customDomain) ? "" : "www.";
    customDomain = `${prefix}${customDomain}`;
    getAnalyticsDataAsync({
      accessToken: props.auth.accessToken,
      data: {
        domain: customDomain,
        type: "referrers",
      },
    })
      .then((response) => {
        // console.log("response.data: ", response.data);
        const rawData = _.get(response.data, "data", []);

        const formattedData = rawData
          .map((item: any) => {
            if (
              (item.referer || "").toString().includes(".txt") ||
              (item.referer || "").toString().includes(".xml") ||
              (item.referer || "").toString().includes(".js") ||
              (item.referer || "").toString().includes(".css")
            ) {
              return null;
            }
            const domain = getActiveDomain(
              props.getCurrentStoreData().currentWebsite
            );
            if ((item.referer || "").toString().includes(domain)) {
              return null;
            }
            if (item.referer === "?") {
              return null;
            }
            return {
              name: item.referer,
              views: parseInt(item.count),
            };
          })
          .filter((item: any) => !!item);

        setData(formattedData);
      })
      .catch((error) => {
        console.error(error);
        console.error(error.response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getReferrersData();
  }, []);

  const truncateText = (text: string, maxLength: number = 10) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  if (isLoading) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon type="loading" style={{ fontSize: 24, color: "#40A9FF" }} />
      </div>
    );
  }

  const formattedData = data.map((item: any) => ({
    ...item,
    displayName: truncateText(item.name),
  }));

  if (formattedData.length === 0) {
    return (
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
        }}
      >
        <Empty description="No data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={formattedData}
        layout="vertical"
        margin={{ top: 0, right: 88, left: 0, bottom: 0 }}
        style={{ background: "transparent" }}
      >
        <XAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#76767b" }}
          hide
        />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#76767b" }}
          hide
        />
        <Tooltip
          cursor={false}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const month = payload[0].payload.name;
              return (
                <div
                  style={{
                    background: "white",
                    padding: "4px 8px",
                    border: "1px solid #F0F4F7",
                    borderRadius: "6px",
                    boxShadow:
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 400,
                      marginBottom: "2px",
                      lineHeight: "1.2",
                    }}
                  >
                    {month}
                  </div>
                  <div
                    style={{
                      opacity: 0.6,
                      fontSize: "12px",
                      lineHeight: "1.2",
                    }}
                  >
                    {payload[0].value} <span>views</span>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <Bar dataKey="views" fill="#303030" radius={6} minPointSize={100}>
          <LabelList
            dataKey="displayName"
            position="insideLeft"
            offset={8}
            fontSize={12}
            fill="#fff"
          />
          <LabelList
            dataKey="views"
            position="right"
            offset={8}
            fontSize={12}
            fill="#303030"
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const BasicAnalytics = (props: Props) => {
  const { history, getCurrentStoreData, websites } = props;
  const currentAppSection = getPathArray(history.location.pathname)[1];
  const { currentWebsite, currentWebsitePage } = getCurrentStoreData();
  const [totalViewsData, setTotalViewsData] = useState([]);
  const [pageViewsData, setPageViewsData] = useState([]);
  const [countriesData, setCountriesData] = useState([]);
  const [referrersData, setReferrersData] = useState([]);

  const getTotalViews = () => {
    return totalViewsData.reduce((acc, curr) => acc + curr.views, 0);
  };

  if (!websites.dataFetched || !currentWebsite) {
    return null;
  }

  return (
    <div
      className="basic-analytics"
      style={{
        display:
          currentAppSection === "analytics" &&
          websites.dataFetched &&
          !!currentWebsite
            ? "flex"
            : "none",
      }}
    >
      <div
        className="basic-analytics__container"
        style={{
          height:
            pageViewsData.length <= 5
              ? 260
              : 260 + (pageViewsData.length - 5) * 20,
        }}
      >
        <ItemCard
          title="Total monthly views"
          bigText={
            totalViewsData.length > 0 ? getTotalViews().toLocaleString() : null
          }
          style={{ paddingBottom: 9 }}
        >
          <ViewsChart
            data={totalViewsData}
            setData={setTotalViewsData}
            {...props}
          />
        </ItemCard>
        <ItemCard title="Top pages">
          <PageViewsChart
            data={pageViewsData}
            setData={setPageViewsData}
            {...props}
          />
        </ItemCard>
      </div>
      <div
        className="basic-analytics__container"
        style={{
          height:
            referrersData.length <= 5
              ? 260
              : 260 + (referrersData.length - 5) * 20,
        }}
      >
        <ItemCard title="Top referrers">
          <ReferrersChart
            data={referrersData}
            setData={setReferrersData}
            {...props}
          />
        </ItemCard>
        <ItemCard title="Top countries" style={{ paddingBottom: 21 }}>
          <CountriesChart
            data={countriesData}
            outerRadius={
              referrersData.length <= 5
                ? 88
                : 88 + (referrersData.length - 5) * 4
            }
            setData={setCountriesData}
            {...props}
          />
        </ItemCard>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  auth: state.auth,
  websites: state.websites,
});

export default withRouter(
  connect(mapStateToProps, { getCurrentStoreData })(BasicAnalytics)
);
